import { useState, useEffect }  from 'react'
import { useSearchParams }       from 'react-router-dom'
import { useTranslation }        from '../i18n/LanguageContext'
import { useAuth }               from '../contexts/AuthContext'
import { subscriptions, users, auth, ApiError, type SubscriptionPrices } from '../api/client'
import Footer from './Footer'

interface SubscriptionPageProps {
  onBack: () => void
}

type Tier = 'BASIC' | 'PLUS' | 'PREMIUM'
const TIERS: Tier[] = ['BASIC', 'PLUS', 'PREMIUM']
const TIER_ORDER: Record<Tier, number> = { BASIC: 0, PLUS: 1, PREMIUM: 2 }

const TIER_STYLE: Record<Tier, {
  emoji:    string
  key:      'basic' | 'plus' | 'premium'
  gradient: string
  accent:   string
  border:   string
  shadow:   string
}> = {
  BASIC:   { emoji: '🌱', key: 'basic',   gradient: 'linear-gradient(135deg, #52B788, #2D6A4F)', accent: '#EEF8F3', border: '#B7E4C7', shadow: 'rgba(82,183,136,0.18)' },
  PLUS:    { emoji: '🌿', key: 'plus',    gradient: 'linear-gradient(135deg, #D4A017, #B5841A)', accent: '#FDF8E8', border: '#F0D890', shadow: 'rgba(212,160,23,0.18)' },
  PREMIUM: { emoji: '🌳', key: 'premium', gradient: 'linear-gradient(135deg, #1B4332, #2D6A4F)', accent: '#EEF4F1', border: '#A8CFC0', shadow: 'rgba(27,67,50,0.22)' },
}

type Banner = 'success' | 'canceled' | 'error' | null

function formatPrice(amount: number | null, currency: string, locale: string): string {
  if (amount === null) return '...'
  return new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-GB', {
    style:                 'currency',
    currency:              currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100)
}

function formatInterval(interval: string, locale: string): string {
  if (locale === 'es') return interval === 'year' ? '/ año' : '/ mes'
  return interval === 'year' ? '/ year' : '/ month'
}

export default function SubscriptionPage({ onBack }: SubscriptionPageProps) {
  const { t, language }           = useTranslation()
  const { user, updateUser }      = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const [loadingTier, setLoadingTier]   = useState<Tier | 'portal' | null>(null)
  // Initialise banner from the URL param set by Stripe on redirect — avoids
  // calling setState synchronously inside an effect (React Compiler rule)
  const [banner, setBanner]             = useState<Banner>(() => {
    const checkout = new URLSearchParams(window.location.search).get('checkout')
    return checkout === 'canceled' ? 'canceled' : null
  })
  const [prices, setPrices]             = useState<SubscriptionPrices | null>(null)
  const [redirectTo, setRedirectTo]     = useState<string | null>(null)

  // Perform external redirects (Stripe Checkout / Customer Portal) in an effect
  // so the React Compiler doesn't flag direct window.location mutation
  useEffect(() => {
    if (redirectTo) window.location.href = redirectTo
  }, [redirectTo])

  const sub         = t.subscription
  const currentTier = (user?.tier ?? 'BASIC') as Tier
  const isPaid      = currentTier !== 'BASIC'

  // Fetch live prices from Stripe
  useEffect(() => {
    subscriptions.prices().then(setPrices).catch(() => { /* keep hardcoded fallback */ })
  }, [])

  // Handle Stripe return: clean up the URL param and, for success, refresh the token
  // so the new tier is reflected immediately. The 'canceled' banner is already set
  // via the useState lazy initialiser above.
  useEffect(() => {
    const result = searchParams.get('checkout')
    if (!result) return
    setSearchParams({}, { replace: true })
    if (result === 'success') {
      auth.refresh().then(() => users.me()).then((fresh) => {
        updateUser({ tier: fresh.tier })
        setBanner('success')
      }).catch(() => setBanner('success'))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUpgrade(tier: 'PLUS' | 'PREMIUM') {
    setLoadingTier(tier)
    setBanner(null)
    try {
      const { url } = await subscriptions.checkout(tier)
      setRedirectTo(url)
    } catch (err) {
      console.error('Checkout error:', err instanceof ApiError ? err.message : err)
      setBanner('error')
      setLoadingTier(null)
    }
  }

  async function handleManage() {
    setLoadingTier('portal')
    setBanner(null)
    try {
      const { url } = await subscriptions.portal()
      setRedirectTo(url)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err)
      console.error('Portal error:', msg)
      setBanner('error')
      setLoadingTier(null)
    }
  }

  const bannerText = banner === 'success'  ? sub.successBanner
                   : banner === 'canceled' ? sub.canceledBanner
                   : banner === 'error'    ? sub.errorBanner
                   : null

  const bannerColor = banner === 'success'  ? '#2D6A4F'
                    : banner === 'canceled' ? '#9A8070'
                    : '#B5481B'

  return (
    <div style={s.root}>
      {/* ── Hero Header ── */}
      <header style={s.hero}>
        <div style={s.heroInner}>
          <button onClick={onBack} style={s.backBtn} aria-label={sub.backBtn}>
            {sub.backBtn}
          </button>
          <h1 style={s.heroTitle}>{sub.title}</h1>
          <p style={s.heroSubtitle}>{sub.subtitle}</p>
        </div>
        <div style={s.heroDecor1} aria-hidden />
        <div style={s.heroDecor2} aria-hidden />
      </header>

      {/* ── Return banner (after Stripe redirect) ── */}
      {bannerText && (
        <div
          role="status"
          aria-live="polite"
          style={{ ...s.banner, background: bannerColor }}
        >
          {bannerText}
        </div>
      )}

      {/* ── Tier cards ── */}
      <main style={s.main}>
        <div style={s.tiersGrid}>
          {TIERS.map((tier, i) => {
            const meta         = TIER_STYLE[tier]
            const plan         = sub.tiers[meta.key]
            const isCurrent    = tier === currentTier
            const isBelow      = TIER_ORDER[tier] < TIER_ORDER[currentTier]
            const isUpgrade    = TIER_ORDER[tier] > TIER_ORDER[currentTier]
            const isLoading    = loadingTier === tier

            // Resolve price from Stripe or fall back to translation
            const stripePrice  = tier === 'PLUS' ? prices?.plus : tier === 'PREMIUM' ? prices?.premium : null
            const displayPrice = stripePrice
              ? formatPrice(stripePrice.amount, stripePrice.currency, language)
              : plan.price
            const displayPeriod = stripePrice
              ? formatInterval(stripePrice.interval, language)
              : plan.period

            return (
              <div
                key={tier}
                className="fade-up"
                style={{
                  ...s.tierCard,
                  background: meta.accent,
                  border: `2px solid ${isCurrent ? meta.border : '#EFE7D7'}`,
                  boxShadow: isCurrent
                    ? `0 6px 28px ${meta.shadow}`
                    : '0 2px 14px rgba(27,67,50,0.07)',
                  animationDelay: `${i * 0.1}s`,
                }}
              >
                {/* Current plan ribbon */}
                {isCurrent && (
                  <div style={{ ...s.currentRibbon, background: meta.gradient }}>
                    {sub.currentPlan}
                  </div>
                )}

                {/* Tier header band */}
                <div style={{ ...s.tierHeader, background: meta.gradient }}>
                  <span style={s.tierEmoji} aria-hidden>{meta.emoji}</span>
                  <span style={s.tierName}>{plan.name}</span>
                </div>

                {/* Price */}
                <div style={s.priceBlock}>
                  <span style={s.priceAmount}>{displayPrice}</span>
                  {displayPeriod && <span style={s.pricePeriod}>{displayPeriod}</span>}
                </div>

                <p style={s.tierDescription}>{plan.description}</p>

                <div style={s.featureDivider} />

                {/* Feature list */}
                <ul style={s.featureList} aria-label={`${plan.name} features`}>
                  {plan.features.map((feat, fi) => (
                    <li key={fi} style={s.featureItem}>
                      <span style={s.featureCheck} aria-hidden>✓</span>
                      <span style={s.featureText}>{feat}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {isCurrent && isPaid ? (
                  // Current paid plan → Manage button
                  <button
                    onClick={handleManage}
                    disabled={loadingTier !== null}
                    style={{ ...s.ctaBtn, ...s.ctaManage }}
                    aria-busy={loadingTier === 'portal'}
                  >
                    {loadingTier === 'portal' ? sub.loadingCheckout : sub.manageBtn}
                  </button>
                ) : isCurrent ? (
                  // BASIC and on BASIC
                  <button disabled style={{ ...s.ctaBtn, ...s.ctaCurrent }}>
                    {sub.currentPlan}
                  </button>
                ) : isBelow ? (
                  // Lower tier than current — already included
                  <button disabled style={{ ...s.ctaBtn, ...s.ctaIncluded }}>
                    {sub.includedPlan}
                  </button>
                ) : isUpgrade ? (
                  // Higher tier — upgradeable
                  <button
                    onClick={() => handleUpgrade(tier as 'PLUS' | 'PREMIUM')}
                    disabled={loadingTier !== null}
                    style={{ ...s.ctaBtn, ...s.ctaUpgrade, background: meta.gradient, color: '#FFF' }}
                    aria-busy={isLoading}
                  >
                    {isLoading ? sub.loadingCheckout : sub.upgradeBtn}
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>
      </main>
      <Footer />
    </div>
  )
}

/* ── Styles ──────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:     '100vh',
    display:       'flex',
    flexDirection: 'column',
    background:    '#FAF6EF',
  },

  // ── Hero ──
  hero: {
    position:   'relative',
    background: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)',
    padding:    '28px 28px 52px',
    overflow:   'hidden',
  },
  heroInner: {
    position: 'relative',
    zIndex:   1,
    maxWidth: 960,
    margin:   '0 auto',
  },
  backBtn: {
    display:      'inline-flex',
    alignItems:   'center',
    fontSize:     '0.95rem',
    fontWeight:   700,
    color:        'rgba(255,255,255,0.75)',
    background:   'rgba(255,255,255,0.1)',
    border:       '1px solid rgba(255,255,255,0.18)',
    borderRadius: 10,
    padding:      '9px 16px',
    cursor:       'pointer',
    marginBottom: 28,
    minHeight:    44,
  },
  heroTitle: {
    fontFamily:   "'Cormorant Garamond', serif",
    fontSize:     'clamp(2rem, 5vw, 3rem)',
    fontWeight:   700,
    color:        '#FFFFFF',
    lineHeight:   1.1,
    marginBottom: 10,
  },
  heroSubtitle: {
    fontFamily: 'Nunito, sans-serif',
    fontSize:   '1.1rem',
    color:      'rgba(255,255,255,0.72)',
    fontWeight: 500,
  },
  heroDecor1: {
    position:      'absolute',
    top:           -80,
    right:         -80,
    width:         280,
    height:        280,
    borderRadius:  '50%',
    background:    'rgba(255,255,255,0.04)',
    pointerEvents: 'none',
  },
  heroDecor2: {
    position:      'absolute',
    bottom:        -40,
    left:          '35%',
    width:         200,
    height:        200,
    borderRadius:  '50%',
    background:    'rgba(255,255,255,0.03)',
    pointerEvents: 'none',
  },

  // ── Banner ──
  banner: {
    padding:    '14px 24px',
    color:      '#FFF',
    fontWeight: 600,
    fontSize:   '1rem',
    textAlign:  'center',
  },

  // ── Main ──
  main: {
    flex:     1,
    maxWidth: 960,
    width:    '100%',
    margin:   '0 auto',
    padding:  '40px 24px 64px',
  },

  tiersGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap:                 24,
    alignItems:          'start',
  },

  // ── Tier card ──
  tierCard: {
    borderRadius:  20,
    overflow:      'hidden',
    display:       'flex',
    flexDirection: 'column',
    position:      'relative',
  },

  currentRibbon: {
    fontSize: '0.875rem',
    fontWeight:    800,
    textTransform: 'uppercase',
    letterSpacing: '0.10em',
    color:         '#FFFFFF',
    textAlign:     'center',
    padding:       '7px 16px',
  },

  tierHeader: {
    display:    'flex',
    alignItems: 'center',
    gap:        10,
    padding:    '20px 24px',
  },
  tierEmoji: {
    fontSize:   '2rem',
    lineHeight: 1,
  },
  tierName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '1.9rem',
    fontWeight: 700,
    color:      '#FFFFFF',
    lineHeight: 1,
  },

  priceBlock: {
    display:    'flex',
    alignItems: 'baseline',
    gap:        6,
    padding:    '24px 24px 8px',
  },
  priceAmount: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '2.6rem',
    fontWeight: 700,
    color:      '#1B4332',
    lineHeight: 1,
  },
  pricePeriod: {
    fontSize:   '1rem',
    fontWeight: 600,
    color:      '#9A8070',
  },

  tierDescription: {
    fontSize:   '0.95rem',
    fontWeight: 500,
    color:      '#6B4C3B',
    lineHeight: 1.55,
    padding:    '8px 24px 20px',
  },

  featureDivider: {
    height:     1,
    background: 'rgba(27,67,50,0.12)',
    margin:     '0 24px',
  },

  featureList: {
    listStyle:     'none',
    padding:       '20px 24px',
    margin:        0,
    display:       'flex',
    flexDirection: 'column',
    gap:           12,
    flex:          1,
  },
  featureItem: {
    display:    'flex',
    alignItems: 'flex-start',
    gap:        10,
  },
  featureCheck: {
    fontSize:   '1rem',
    fontWeight: 800,
    color:      '#2D6A4F',
    lineHeight: 1.4,
    flexShrink: 0,
  },
  featureText: {
    fontSize:   '1rem',
    fontWeight: 500,
    color:      '#2E1F14',
    lineHeight: 1.4,
  },

  ctaBtn: {
    margin:        '8px 24px 24px',
    padding:       '14px 20px',
    borderRadius:  12,
    fontSize:      '1rem',
    fontWeight:    800,
    letterSpacing: '0.03em',
    border:        'none',
    minHeight:     52,
    transition:    'opacity 0.15s',
    cursor:        'pointer',
  },
  ctaCurrent: {
    background: 'rgba(27,67,50,0.10)',
    color:      '#2D6A4F',
    cursor:     'default',
  },
  ctaIncluded: {
    background: 'rgba(27,67,50,0.06)',
    color:      '#7AAF92',
    cursor:     'default',
    fontWeight: 600,
    fontSize:   '0.9rem',
  },
  ctaUpgrade: {
    cursor: 'pointer',
  },
  ctaManage: {
    background: 'rgba(27,67,50,0.10)',
    color:      '#2D6A4F',
    cursor:     'pointer',
  },
}
